import { getLocalStorageItem, setLocalStorageItem } from '@common/core-layer/utils'
import { useHead, useState } from 'nuxt/app'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { Theme } from '../types/theme'
import { pickOnColor } from '../utils/color'
import { applyColorScale, getColorScale, removeColorScale } from '../utils/color-scale'

// imports at top per .cursorrules

const THEME_STORAGE_KEY = 'cp-theme-preference'

const TOKEN_CSS_MAP: Record<keyof BrandTokens, string> = {
  primary: '--cp-primary',
  secondary: '--cp-secondary',
  tertiary: '--cp-tertiary',
  background: '--cp-background',
  surface: '--cp-surface',
  error: '--cp-error',
  fontColor: '--cp-font',
  sidebar: '--cp-sidebar',
  border: '--cp-border',
}

const BRAND_CSS_MAP: Record<keyof BrandTokens, string> = {
  primary: '--cp-brand-primary',
  secondary: '--cp-brand-secondary',
  tertiary: '--cp-brand-tertiary',
  background: '--cp-brand-background',
  surface: '--cp-brand-surface',
  error: '--cp-brand-error',
  fontColor: '--cp-brand-font',
  sidebar: '--cp-brand-sidebar',
  border: '--cp-brand-border',
}

const TOKEN_SCALE_NAMES: Record<keyof BrandTokens, string> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  background: 'background',
  surface: 'surface',
  error: 'error',
  fontColor: 'font',
  sidebar: 'sidebar',
  border: 'border',
}

/**
 * HighRise (@platform-ui/highrise) defines its own root-level scales like
 * `--primary-50 … --primary-900` that every HL component reads internally
 * (HLButton, HLCheckbox, HLRadio, HLTabs, HLCalendar, etc.). Those defaults
 * are always blue regardless of the active brand theme, which is why
 * patching per-CP component `:style` overrides never scales.
 *
 * We bridge HighRise's prefixes to our `--cp-*` scales so the entire
 * HL component tree picks up the active brand primary automatically.
 *
 * Only `primary` and `error` are bridged because those are the only two
 * prefixes HighRise consumes at component level (focus rings, borders,
 * active states, danger variants). Other tokens (`background`, `surface`,
 * `tertiary`, `sidebar`, `fontColor`) are applied directly via our own
 * `--cp-*` variables in component styles and do not need a HighRise bridge.
 */
const HIGHRISE_BRIDGE_MAP: Partial<Record<keyof BrandTokens, string>> = {
  primary: '--primary',
  error: '--error',
}

const DEFAULT_FONT_SCALE = {
  50: '#f3f4f6',
  100: '#f2f4f7',
  200: '#d1d5db',
  300: '#9ca3af',
  400: '#98a2b3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1d2939',
  900: '#101828',
} as const

// theme-layer/composables/useTheme.ts
export const useTheme = () => {
  const currentTheme = useState<Theme>('cp-current-theme', getInitialTheme)
  const activeBrandTokens = useState<BrandTokens | null>('cp-active-brand-tokens', () => null)
  const themeRootStyle = useState('cp-theme-root-style', () => '')

  useHead(() => ({
    style: themeRootStyle.value ? [{ key: 'cp-brand-theme', innerHTML: themeRootStyle.value }] : [],
  }))

  // Get theme from localStorage (for non-logged-in users)
  function getStoredTheme(): Theme {
    if (process.server) return Theme.LIGHT
    const stored = getLocalStorageItem(THEME_STORAGE_KEY)
    if (stored === Theme.DARK || stored === Theme.LIGHT) {
      return stored as Theme
    }
    return Theme.LIGHT // Default to light mode
  }

  // Store theme in localStorage (for non-logged-in users)
  function storeTheme(theme: Theme) {
    if (process.server) return
    setLocalStorageItem(THEME_STORAGE_KEY, theme)
  }

  function getInitialTheme(): Theme {
    // if (process.server) return Theme.LIGHT

    // // Try to get from localStorage first (for non-logged-in users)
    // const stored = getStoredTheme()
    // if (stored) {
    //   return stored
    // }

    // Default to light mode
    return Theme.LIGHT
  }

  function applyTheme(theme: Theme) {
    if (process.server) return
    document.documentElement.setAttribute('data-theme', theme)
    currentTheme.value = theme
  }

  // Lazy get auth and user stores - only when needed to avoid initialization order issues
  function getAuthStore() {
    try {
      return useAuth().store
    } catch {
      return null
    }
  }

  function getCurrentUserStore() {
    try {
      return useCurrentUser()
    } catch {
      return null
    }
  }

  async function setTheme(theme: Theme, persist: boolean = true) {
    // Apply attribute early to let CSS respond immediately
    applyTheme(theme)
    if (process.server) return

    // Persist theme preference
    if (persist) {
      const authStore = getAuthStore()
      const currentUser = getCurrentUserStore()

      if (authStore?.isLoggedIn && currentUser?.store?.profile) {
        // User is logged in: update their profile
        const newDarkMode = theme === Theme.DARK
        try {
          await currentUser.updateProfile({
            darkMode: newDarkMode,
          })
        } catch (error) {
          console.error('Failed to update dark mode preference:', error)
        }
      } else {
        // User is not logged in: store in localStorage
        storeTheme(theme)
      }
    }

    const root = document.documentElement

    // Remove inline brand token styles to allow CSS theme.css to take over
    // The CSS file defines different values for light/dark via [data-theme] selector
    clearBrandTokenOverrides(root)

    // Use requestAnimationFrame to ensure CSS has re-evaluated after attribute change
    requestAnimationFrame(() => {
      if (activeBrandTokens.value) {
        applyBrandTokens(activeBrandTokens.value, currentTheme.value, themeRootStyle)
      } else {
        themeRootStyle.value = ''
      }
    })
  }

  function setBrandTokens(tokens: Partial<BrandTokens>) {
    const resolvedTokens = resolveBrandTokens(tokens, activeBrandTokens.value)
    if (!resolvedTokens) return

    activeBrandTokens.value = resolvedTokens
    applyBrandTokens(resolvedTokens, currentTheme.value, themeRootStyle)
  }

  function resetBrandTokens() {
    activeBrandTokens.value = null
    themeRootStyle.value = ''

    if (process.server) return

    clearBrandTokenOverrides(document.documentElement)
  }

  return {
    activeBrandTokens,
    currentTheme,
    getStoredTheme,
    setTheme,
    setBrandTokens,
    resetBrandTokens,
  }
}

export { Theme }

export interface BrandTokens {
  primary: string
  // Optional secondary brand accent. Clientportal sets it from portal brandColor2;
  // community/preset tokens omit it and fall back to the theme.css `--cp-secondary-*`
  // defaults. Not part of the completeness gate so existing 8-key literals stay valid.
  secondary?: string
  tertiary: string
  background: string
  surface: string
  error: string
  fontColor: string
  sidebar: string
  border: string
}

function collectCurrentBrandTokens(root: HTMLElement): BrandTokens | null {
  const get = (name: string) => getComputedStyle(root).getPropertyValue(name).trim()
  const primary = get('--cp-brand-primary')
  // Read secondary from INLINE style only (not the computed cascade). theme.css
  // always defines a `--cp-brand-secondary` default, so getComputedStyle would
  // never be empty and would reconstruct a secondary the surface never set.
  const secondary = root.style.getPropertyValue('--cp-brand-secondary').trim()
  const tertiary = get('--cp-brand-tertiary')
  const background = get('--cp-brand-background')
  const surface = get('--cp-brand-surface')
  const error = get('--cp-brand-error')
  const fontColor = get('--cp-brand-font')
  const sidebar = get('--cp-brand-sidebar')
  const border = get('--cp-brand-border')
  // `secondary` is optional and excluded from the completeness gate.
  if (!primary || !tertiary || !background || !surface || !error || !fontColor || !sidebar || !border) return null
  return { primary, tertiary, background, surface, error, fontColor, sidebar, border, ...(secondary ? { secondary } : {}) }
}

function hasCompleteBrandTokens(tokens: Partial<BrandTokens>): tokens is BrandTokens {
  return Boolean(
    tokens.primary &&
      tokens.tertiary &&
      tokens.background &&
      tokens.surface &&
      tokens.error &&
      tokens.fontColor &&
      tokens.sidebar &&
      tokens.border
  )
}

function mergeBrandTokens(base: BrandTokens, tokens: Partial<BrandTokens>): BrandTokens {
  const merged = { ...base, ...tokens }

  // `secondary` is an optional, per-surface accent. A COMPLETE token set that
  // omits it (community/preset themes always send the 8 required keys with no
  // secondary) must not inherit a previous surface's secondary — otherwise a
  // clientportal brandColor2 would bleed into a community theme via this merge.
  // Spreading `undefined` does not delete the key, so drop it explicitly.
  if (hasCompleteBrandTokens(tokens) && tokens.secondary === undefined) {
    delete merged.secondary
  }

  return merged
}

function resolveBrandTokens(tokens: Partial<BrandTokens>, currentTokens: BrandTokens | null): BrandTokens | null {
  if (currentTokens) {
    return mergeBrandTokens(currentTokens, tokens)
  }

  if (process.server) {
    return hasCompleteBrandTokens(tokens) ? tokens : null
  }

  const runtimeTokens = collectCurrentBrandTokens(document.documentElement)

  if (runtimeTokens) {
    return mergeBrandTokens(runtimeTokens, tokens)
  }

  return hasCompleteBrandTokens(tokens) ? tokens : null
}

function buildThemeRootCss(tokens: BrandTokens, mode: Theme): string {
  const decls: string[] = []

  for (const [key, cssVar] of Object.entries(BRAND_CSS_MAP)) {
    const value = tokens[key as keyof BrandTokens]
    if (value) {
      decls.push(`${cssVar}:${value}`)
    }
  }

  for (const [key, cssPrefix] of Object.entries(TOKEN_CSS_MAP)) {
    const hex = tokens[key as keyof BrandTokens]
    if (hex) {
      const tokenName = TOKEN_SCALE_NAMES[key as keyof BrandTokens]
      const scale = key === 'fontColor' ? getFontScaleFromBrand(hex, mode) : getColorScale(hex, tokenName, mode)

      const highrisePrefix = HIGHRISE_BRIDGE_MAP[key as keyof BrandTokens]

      for (const [step, value] of Object.entries(scale)) {
        if (value) {
          decls.push(`${cssPrefix}-${step}:${value}`)
          if (highrisePrefix) {
            decls.push(`${highrisePrefix}-${step}:${value}`)
          }
        }
      }
    }
  }

  decls.push('--cp-text-on-primary:' + pickOnColor(tokens.primary))
  if (tokens.secondary) {
    decls.push('--cp-text-on-secondary:' + pickOnColor(tokens.secondary))
  }
  decls.push('--cp-text-on-tertiary:' + pickOnColor(tokens.tertiary))
  decls.push('--cp-text-on-error:' + pickOnColor(tokens.error))
  decls.push('--cp-text-on-sidebar:' + pickOnColor(tokens.sidebar))

  // Use `:root:root` (specificity 0,0,2,0) so the brand bridge always wins over
  // HighRise's shipped `:root{--primary-*: …}` defaults regardless of stylesheet load order.
  return ':root:root{' + decls.join(';') + '}'
}

function applyBrandTokens(tokens: BrandTokens, mode: Theme, themeRootStyle: { value: string }) {
  themeRootStyle.value = buildThemeRootCss(tokens, mode)

  if (process.server) return

  const root = document.documentElement

  for (const [key, cssVar] of Object.entries(BRAND_CSS_MAP)) {
    const value = tokens[key as keyof BrandTokens]
    if (value) {
      root.style.setProperty(cssVar, value)
    } else {
      // Optional token (secondary) absent: clear any stale inline value so a prior
      // surface's colour cannot linger (e.g. portal secondary bleeding into a community).
      root.style.removeProperty(cssVar)
    }
  }

  // Set each token scale (e.g. surface → --cp-surface-50 … --cp-surface-900) from brand hex
  for (const [key, cssPrefix] of Object.entries(TOKEN_CSS_MAP)) {
    const hex = tokens[key as keyof BrandTokens]
    if (hex) {
      const tokenName = TOKEN_SCALE_NAMES[key as keyof BrandTokens]
      const scale = key === 'fontColor' ? getFontScaleFromBrand(hex, mode) : getColorScale(hex, tokenName, mode)
      applyColorScale(root, cssPrefix, scale)

      const highrisePrefix = HIGHRISE_BRIDGE_MAP[key as keyof BrandTokens]
      if (highrisePrefix) {
        applyColorScale(root, highrisePrefix, scale)
      }
    } else {
      // Optional token absent: drop its stale inline scale so the theme.css default
      // (via the cascade) takes over instead of the previous surface's colour.
      removeColorScale(root, cssPrefix)
    }
  }

  // intentionally commenting out next line because we want to disable custom sidebar color
  // Historical note: sidebar color is now applied by the shared theme writer so community branding stays consistent.

  computeOnColors(root, tokens)
}

function clearBrandTokenOverrides(root: HTMLElement) {
  for (const cssVar of Object.values(BRAND_CSS_MAP)) {
    root.style.removeProperty(cssVar)
  }

  for (const cssPrefix of Object.values(TOKEN_CSS_MAP)) {
    removeColorScale(root, cssPrefix)
  }

  for (const cssPrefix of Object.values(HIGHRISE_BRIDGE_MAP)) {
    if (cssPrefix) {
      removeColorScale(root, cssPrefix)
    }
  }

  root.style.removeProperty('--cp-text-on-primary')
  root.style.removeProperty('--cp-text-on-secondary')
  root.style.removeProperty('--cp-text-on-tertiary')
  root.style.removeProperty('--cp-text-on-error')
  root.style.removeProperty('--cp-text-on-sidebar')
}

function computeOnColors(root: HTMLElement, tokens: BrandTokens) {
  const onPrimary = pickOnColor(getComputedStyle(root).getPropertyValue('--cp-primary-600') || tokens.primary)
  root.style.setProperty('--cp-text-on-primary', onPrimary)
  if (tokens.secondary) {
    const onSecondary = pickOnColor(getComputedStyle(root).getPropertyValue('--cp-secondary-600') || tokens.secondary)
    root.style.setProperty('--cp-text-on-secondary', onSecondary)
  } else {
    // Clear stale inline contrast colour so it doesn't outlive its secondary.
    root.style.removeProperty('--cp-text-on-secondary')
  }
  const onTertiary = pickOnColor(getComputedStyle(root).getPropertyValue('--cp-tertiary-600') || tokens.tertiary)
  root.style.setProperty('--cp-text-on-tertiary', onTertiary)
  const onError = pickOnColor(getComputedStyle(root).getPropertyValue('--cp-error-600') || tokens.error)
  root.style.setProperty('--cp-text-on-error', onError)
  const onSidebar = pickOnColor(getComputedStyle(root).getPropertyValue('--cp-sidebar-500') || tokens.sidebar)
  root.style.setProperty('--cp-text-on-sidebar', onSidebar)
}

function getFontScaleFromBrand(brandFontHex: string, mode: Theme) {
  if (mode === Theme.DARK) {
    return getColorScale(brandFontHex, 'font', mode)
  }

  const normalizedBrand = normalizeHex(brandFontHex)
  if (normalizedBrand === DEFAULT_FONT_SCALE[800] || normalizedBrand === DEFAULT_FONT_SCALE[900]) {
    return { ...DEFAULT_FONT_SCALE }
  }

  const brandRgb = hexToRgb(normalizedBrand)
  const default800Rgb = hexToRgb(DEFAULT_FONT_SCALE[800])
  if (!brandRgb || !default800Rgb) {
    return getColorScale(brandFontHex, 'font', mode)
  }

  const scale: Record<number, string> = {}
  for (const [stepKey, stepHex] of Object.entries(DEFAULT_FONT_SCALE)) {
    const step = Number(stepKey)
    if (step === 800) {
      scale[step] = normalizedBrand
      continue
    }

    const stepRgb = hexToRgb(stepHex)
    if (!stepRgb) continue

    scale[step] = rgbToHex({
      r: clamp(brandRgb.r + (stepRgb.r - default800Rgb.r)),
      g: clamp(brandRgb.g + (stepRgb.g - default800Rgb.g)),
      b: clamp(brandRgb.b + (stepRgb.b - default800Rgb.b)),
    })
  }

  return scale
}

function normalizeHex(hex: string): string {
  const trimmed = hex.trim().toLowerCase()
  if (!trimmed.startsWith('#')) return trimmed
  if (trimmed.length === 4) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
  }
  return trimmed
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHex(hex)
  if (!/^#[0-9a-f]{6}$/.test(normalized)) return null

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  }
}

function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const toHex = (value: number) => value.toString(16).padStart(2, '0')
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

function clamp(value: number): number {
  return Math.max(0, Math.min(255, value))
}
